import { useRouter } from 'next/router';
import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBeforeUnload } from 'react-use';
import { UnsavedChangesContext } from './UnsavedChangesContext';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

type ShouldBlockNavigationCallback = (
  newRoute: string,
  currentRoute: string,
) => boolean;

const defaultShouldBlockNavigation: ShouldBlockNavigationCallback = (
  newRoute,
  currentRoute,
) => {
  return currentRoute !== newRoute.replace('/txm', '');
};

export type UnsavedChangesProviderProps = {
  /**
   * Text to use for the cancel button
   */
  cancelButtonText?: string;
  children?: ReactNode;
  /**
   * A custom component to use, which must consume the `UnsavedChangesContext`
   */
  dialogComponent?: ComponentType;
  /**
   * Content for the dialog body
   */
  dialogBody?: ReactNode;
  /**
   * Text to use for the dialog title
   */
  dialogTitle?: string;
  /**
   * Function to invoke when the changes are preserved
   */
  onCancel?: () => void;
  /**
   * Function to invoke when the changes are discarded
   */
  onContinue?: () => void;
  /**
   * Text to use for the submit button
   */
  submitButtonText?: string;
  /**
   * Indicates whether the page has unsaved changes. If left unspecified, the internal state will be used.
   */
  isPageDirty?: boolean;
  /**
   * Custom function to determine if navigation should be blocked.
   */
  shouldBlockNavigation?: ShouldBlockNavigationCallback;
};

/**
 * Catches any attempts to leave a page that has unsaved changes. This is implemented at the _app root level, meaning you can use it anywhere within TXM simply by importing the respective context and setting up a `React.useEffect` to update the context state whenever a page transitions between a clean/dirty state.
 *
 * You can also render this component elsewhere if you need a custom dialog for a certain feature. You'll simply need to pass in the alternative dialog component via the `dialogComponent` prop. Your custom dialog will need to consume the `UnsavedChangesContext` similar to how `UnsavedChangesDialog` is implemented.
 *
 * @example
 * // Using the default dialog
 * import { useContext, useEffect } from 'react';
 * import { UnsavedChangesContext } from '@/components/UnsavedChanges';
 *
 * const { setIsPageDirty } = useContext(UnsavedChangesContext);
 * const isDirty = foo && bar;
 *
 * useEffect(() => {
 *   setIsPageDirty(isDirty);
 * }, [isDirty, setIsPageDirty]);
 *
 * @example
 * // Using a custom dialog
 * import { UnsavedChangesProvider } from '@/components/UnsavedChanges';
 *
 * <UnsavedChangesProvider dialogComponent={CustomUnsavedChangesDialog}>
 *   <CoolFeatureWithAForm />
 * </UnsavedChangesProvider>
 *
 * @example
 * // Controlling isPageDirty through props
 * import { useForm } from 'react-hook-form';
 * import { UnsavedChangesProvider } from '@/components/UnsavedChanges';
 *
 * const methods = useForm();
 * const { formState: { isDirty } } = methods;
 *
 * <FormProvider {...methods}>
 *   <MyFormComponent />
 *   <UnsavedChangesProvider isPageDirty={isDirty} dialogComponent={CustomUnsavedChangesDialog} />
 * </FormProvider>
 *
 */
export const UnsavedChangesProvider = (props: UnsavedChangesProviderProps) => {
  const {
    cancelButtonText = 'Go Back',
    children,
    dialogBody = 'It looks like there are some changes that have not been saved. If you continue without saving first, all changes will be lost.',
    dialogComponent: Dialog = UnsavedChangesDialog,
    dialogTitle = 'Continue Without Saving?',
    onCancel: onCancelProp = undefined,
    onContinue: onContinueProp = undefined,
    submitButtonText = 'Discard Changes & Continue',
    isPageDirty: isPageDirtyProp,
    shouldBlockNavigation = defaultShouldBlockNavigation,
  } = props;

  const router = useRouter();
  const [internalIsPageDirty, setInternalIsPageDirty] = useState(false);
  const [nextRoute, setNextRoute] = useState('');
  const allowRouteChange = useRef(false);
  const isPageDirty = isPageDirtyProp ?? internalIsPageDirty;
  const isDialogOpen = Boolean(isPageDirty && nextRoute);

  const onCancel = useCallback(() => {
    onCancelProp?.();
    setNextRoute('');

    const currentUrl = router.basePath + router.asPath;

    if ((window?.history?.state as { url: string })?.url !== currentUrl) {
      window.history.pushState(
        {
          ...window.history.state,
          url: currentUrl,
          as: currentUrl,
        },
        '',
        currentUrl,
      );
    }

    router.events.emit('routeChangeError');
  }, [onCancelProp, router.asPath, router.basePath, router.events]);

  const navigateWithoutBlocking = useCallback(
    async (route: string) => {
      setInternalIsPageDirty(false);
      allowRouteChange.current = true;
      try {
        await router.push(route);
      } finally {
        allowRouteChange.current = false;
      }
    },
    [router],
  );

  const onContinue = useCallback(async () => {
    onContinueProp?.();
    setNextRoute('');
    await navigateWithoutBlocking(nextRoute.replace('/txm', '') || '/');
  }, [navigateWithoutBlocking, nextRoute, onContinueProp]);

  // This will catch any attempts to close the tab/window or reload the page
  // We cannot control the dialog that is presented, nor can we change the
  // messaging (despite us passing a string here, which is really only for
  // legacy browsers)
  useBeforeUnload(isPageDirty, 'You have unsaved changes, are you sure?');

  useEffect(() => {
    const handler = (newRoute: string) => {
      if (isPageDirty && !allowRouteChange.current) {
        if (shouldBlockNavigation(newRoute, router.pathname)) {
          setNextRoute(newRoute);

          // biome-ignore lint/style/useThrowOnlyError: Throwing a string literal to prevent triggering the 500 page
          throw 'Canceling route change due to unsaved changes.';
        }
      }
    };

    router.events.on('beforeHistoryChange', handler);

    return () => {
      router.events.off('beforeHistoryChange', handler);
    };
  }, [
    allowRouteChange,
    isPageDirty,
    router.asPath,
    router.events,
    router.pathname,
    shouldBlockNavigation,
  ]);

  const contextValue = useMemo(
    () => ({
      cancelButtonText,
      dialogBody,
      dialogTitle,
      isDialogOpen,
      isPageDirty,
      navigateWithoutBlocking,
      nextRoute,
      onCancel,
      onContinue,
      setIsPageDirty: setInternalIsPageDirty,
      setNextRoute,
      submitButtonText,
    }),
    [
      cancelButtonText,
      dialogBody,
      dialogTitle,
      isDialogOpen,
      isPageDirty,
      navigateWithoutBlocking,
      nextRoute,
      onCancel,
      onContinue,
      submitButtonText,
    ],
  );

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
      <Dialog />
    </UnsavedChangesContext.Provider>
  );
};
